<template>
  <div class="h-full">
    <BaseTable
      v-if="warehouses"
      :headers="tableHeaders"
      :list="filteredWarehouses"
      :total-records="warehousesTotalCount"
      :page="warehousesPageNumber"
      :items-per-page="warehousesItemsPerPage"
      table-id="warehouses"
      is-saving-layout-hidden
      :clickable="mode === 'edit'"
      hide-grouping
      :is-add-new-hidden="mode === 'view'"
      :disable-multiselect="mode === 'view'"
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @refresh-data="onRefreshData"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters"
      @table-item-clicked="onEdit">
      <template #actions="{ item }" v-if="mode == 'edit'">
        <div class="flex items-center justify-center gap-1">
          <button
            v-if="item.isFromManager"
            class="p-1.5 text-red-400 rounded-full hover:bg-red-50"
            type="button"
            @click="onDeleteRow(item)">
            <IconTrashOutline class="size-4" />
          </button>
        </div>
      </template>
      <template #filters>
        <div class="grid grid-cols-3 mx-10 my-5">
          <div class="space-y-4 justify-self-start">
            <div>
              <BaseTriStateToggle
                :label="$t('representative.isMapped')"
                name="isInactiveFilter"
                v-model="filterIsMapped" />
            </div>
          </div>
        </div>
      </template>
    </BaseTable>

    <DCWarehouseModalCreateEdit
      v-if="createEditModalActive && currentWarehouse"
      :view-only="mode === 'view'"
      :warehouse="currentWarehouse"
      :mode="createEditModalMode"
      :representative-id="representativeId"
      :existing-warehouses="warehouses"
      @close-modal="createEditModalActive = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useI18n } from 'vue-i18n';

import BaseTable from '@/components/common/BaseTable.vue';
import DCWarehouseModalCreateEdit from '@/components/traders/DCWarehouseModalCreateEdit.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseTriStateToggle from '../common/BaseTriStateToggle.vue';

import { deepCopy } from '@/utils/data';
import { RecordMode, type TableFilterData } from '@/types/Base';
import { getDCWarehousesWithConditions } from '@/services/api/dataCollection';
import type { DCWarehouse, RepresentativeFiltersData } from '@/types/DataCollection';

const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout?: boolean;
  resetState: boolean;
  representativeId: string;
}>();

const warehouses = defineModel<DCWarehouse[]>('warehouses');
const warehouseModified = defineModel<boolean>('isWarehouseModified', {
  default: false,
});
const filterIsMapped = ref<boolean | undefined>(undefined);

const originalWarehouses = ref<DCWarehouse[]>([]);
const warehousesTotalCount = ref<number>(0);
const warehousesItemsPerPage = ref(50);
const warehousesPageNumber = ref(1);

const filteredWarehouses = computed<DCWarehouse[]>(() => {
  if (!warehouses.value) return [];
  let list = warehouses.value.filter((x) => x.recordMode !== RecordMode.Deleted);

  return list;
});

watch(
  () => warehouses.value,
  (newData) => {
    warehouseModified.value = JSON.stringify(newData) !== JSON.stringify(originalWarehouses.value);
  },
  { deep: true }
);

async function loadWarehouses(
  params?: TableFilterData & {
    isMapped?: boolean;
  }
) {
  const queryParams = {
    representativeId: props.representativeId,
    page: params?.page || warehousesPageNumber.value,
    itemsPerPage: params?.itemsPerPage || warehousesItemsPerPage.value,
    query: params?.query || '',
    sortColumn: params?.column !== undefined ? tableHeaders[params.column]?.value : undefined,
    sortAscending: params?.order === 'asc' || params?.order === undefined,
    isMapped: params?.isMapped,
  };

  const res = await getDCWarehousesWithConditions(queryParams);
  if (res) {
    originalWarehouses.value = res.data;
    warehouses.value = deepCopy<DCWarehouse[]>(originalWarehouses.value);
    warehousesTotalCount.value = res.count;

    if (params?.page !== undefined) {
      warehousesPageNumber.value = params.page;
    }
    if (params?.itemsPerPage !== undefined) {
      warehousesItemsPerPage.value = params.itemsPerPage;
    }
  }
}

watch(
  () => props.resetState,
  async (newVal, oldVal) => {
    if (props.representativeId && (oldVal === undefined || (newVal && oldVal !== newVal))) {
      await loadWarehouses();
    }
  },
  { immediate: true }
);

const { t: $t } = useI18n();

const createEditModalActive = ref(false);
const createEditModalMode = ref<'add' | 'edit'>('add');
const currentWarehouse = ref<DCWarehouse | undefined>(undefined);

const tableHeaders = [
  { value: 'warehouse.name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'warehouse.code', text: $t('baseObject.code'), type: 'text', active: true },
  {
    value: 'warehouseRemotedCode',
    text: $t('representative.remotedCode'),
    type: 'text',
    active: true,
  },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

function initWarehouse(): DCWarehouse {
  return {
    id: uuidv4(),
    representativeId: props.representativeId,
    warehouseRemotedCode: '',
    warehouseId: '',
    warehouse: {
      id: '',
      name: '',
    },
    isActive: true,
    isFromManager: true,
    recordMode: RecordMode.Added,
  };
}

function onAddNew() {
  currentWarehouse.value = initWarehouse();
  createEditModalMode.value = 'add';
  createEditModalActive.value = true;
}

function onEdit(item: DCWarehouse) {
  currentWarehouse.value = deepCopy(item);
  createEditModalMode.value = 'edit';
  createEditModalActive.value = true;
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<DCWarehouse>();

function onDeleteRow(item: DCWarehouse) {
  deleteRowItem.value = deepCopy(item);
  modalDeleteRow.value = true;
}

async function onDeleteSingle() {
  if (!deleteRowItem.value || !warehouses.value) return;
  modalDeleteRow.value = false;
  const index = warehouses.value?.findIndex((x) => x.id === deleteRowItem.value?.id);
  if (index !== -1) {
    const existingItem = warehouses.value[index];
    if (existingItem.recordMode === RecordMode.Added) {
      warehouses.value.splice(index, 1);
    } else if (existingItem.recordMode === RecordMode.Modified) {
      existingItem.recordMode = RecordMode.Deleted;
    } else {
      existingItem.recordMode = RecordMode.Deleted;
    }
  }
}

function onDeleteMultiselect(items: DCWarehouse[]) {
  modalDeleteRow.value = false;

  items.forEach((item) => {
    if (!warehouses.value) return;
    if (item.isFromManager) {
      const existingIndex = warehouses.value.findIndex((x) => x.id === item.id);
      if (existingIndex !== -1) {
        const existingItem = warehouses.value[existingIndex];
        if (existingItem.recordMode === RecordMode.Added) {
          warehouses.value.splice(existingIndex, 1);
        } else if (existingItem.recordMode === RecordMode.Modified) {
          existingItem.recordMode = RecordMode.Deleted;
        } else {
          existingItem.recordMode = RecordMode.Deleted;
        }
      }
    }
  });
}

function onSave(unit: DCWarehouse) {
  if (!warehouses.value) return;
  createEditModalActive.value = false;

  const existingIndex = warehouses.value?.findIndex((x) => x.id === unit.id);
  if (existingIndex !== -1) warehouses.value[existingIndex] = unit;
  else warehouses.value.push(unit);
}

const filterState = reactive<{
  query: string;
  column?: number;
  order?: 'asc' | 'desc';
  isMapped?: boolean;
}>({
  query: '',
  column: undefined,
  order: undefined,
  isMapped: undefined,
});

async function onRefreshData(
  options: TableFilterData & {
    isMapped?: boolean;
  }
) {
  filterState.query = options.query ?? '';
  if (options.column !== undefined && options.order) {
    filterState.column = options.column;
    filterState.order = options.order;
  }
  filterState.isMapped = options.isMapped;

  await loadWarehouses(options);
}

async function onApplyFilters(data: RepresentativeFiltersData) {
  data.isMapped = filterIsMapped.value;
  await onRefreshData(data);
}

async function onResetFilters() {
  filterIsMapped.value = undefined;
  await onRefreshData({});
}
</script>
